import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, Button, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchBusinessProcess } from "../../api/memberApi";

const PROCESS_TYPE_LABELS = {
  CoreProcess: "businessProcess.type.coreProcess",
  BusinessSupportProcess: "businessProcess.type.businessSupportProcess",
  EnterpriseSupportProcess: "businessProcess.type.enterpriseSupportProcess",
};

const PROCESS_CLASS_LABELS = {
  Operation: "businessProcess.class.operation",
  CustomerRelationshipManagement: "businessProcess.class.crm",
  ProductLifeCycleManagement: "businessProcess.class.plm",
  HumanResourceManagement: "businessProcess.class.hrm",
  InvestorRelationshipManagement: "businessProcess.class.irm",
  PartnerRelationshipManagement: "businessProcess.class.prm",
  DataManagement: "businessProcess.class.dm",
  ItServiceManagement: "businessProcess.class.itsm",
  AssetManagement: "businessProcess.class.am",
  Financing: "businessProcess.class.financing",
  GeneralManagement: "businessProcess.class.generalManagement",
};

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}

export default function ValueChainDetailPage() {
  const { businessDomainId, valueChainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBusinessProcess(valueChainId);
      setItem(data);
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
  }, [valueChainId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
  if (!item) return null;

  const processTypeLabel = PROCESS_TYPE_LABELS[item.businessProcessType]
    ? t(PROCESS_TYPE_LABELS[item.businessProcessType])
    : item.businessProcessType || "";
  const processClassLabel = PROCESS_CLASS_LABELS[item.businessProcessClass]
    ? t(PROCESS_CLASS_LABELS[item.businessProcessClass])
    : item.businessProcessClass || "";

  const associatedProcesses = item.associatedBusinessProcesses || [];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/design/${businessDomainId}/value-chains`)} variant="text">
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t("valueChain.detail.title")}</Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <DetailRow label={t("valueChain.name")} value={item.businessProcessName} />
        <DetailRow label={t("businessProcess.type")} value={processTypeLabel} />
        <DetailRow label={t("businessProcess.class")} value={processClassLabel} />
        <DetailRow label={t("valueChain.description")} value={item.businessProcessDescription} />
        <DetailRow label={t("common.url")} value={item.url} />
      </Paper>

      {associatedProcesses.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t("valueChain.process.list.title")}</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t("valueChain.process.name")}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t("valueChain.process.class")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {associatedProcesses.map((p) => {
                  const classLabel = PROCESS_CLASS_LABELS[p.businessProcessClass]
                    ? t(PROCESS_CLASS_LABELS[p.businessProcessClass])
                    : p.businessProcessClass || "—";
                  return (
                    <TableRow key={p.businessProcessId}>
                      <TableCell>{p.businessProcessName || "—"}</TableCell>
                      <TableCell>{classLabel}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
